import { useEffect, useState } from "react";

const ROLES = ["Email Triage Viewer", "System Admin"];

// ── Sub-components ────────────────────────────────────────────────────────────

function ActiveToggle({ isActive, onToggle, loading }) {
  return (
    <button
      type="button"
      className={`ur-toggle ${isActive ? "ur-toggle--on" : "ur-toggle--off"}`}
      onClick={onToggle}
      disabled={loading}
      title={isActive ? "Click to deactivate" : "Click to activate"}
      aria-label={isActive ? "Deactivate user" : "Activate user"}
    >
      <span className="ur-toggle__track">
        <span className="ur-toggle__thumb" />
      </span>
      <span className="ur-toggle__label">{isActive ? "Active" : "Inactive"}</span>
    </button>
  );
}

function AddUserForm({ onAdd, onCancel }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("Email Triage Viewer");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim();
    if (!trimmed) { setError("Email is required."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("Enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/user-roles", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, role }),
      });
      const json = await res.json();
      if (!res.ok) { setError(json.error || "Failed to save user."); return; }
      onAdd(json.data, json.updated);
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      {/* <h4 className="ur-add-form__title">Add New User</h4> */}
      <div className="triage-filter">
        <input
          className="triage-search"
          style={{maxWidth: 50+'%'}}
          type="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
          autoFocus
        />
        <select
          className="triage-select"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          disabled={loading}
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
          <button
            type="submit"
            className="add-btn"
            disabled={loading}
          >
            {loading ? "Adding…" : "Save"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      {error && <p className="ur-add-form__error">{error}</p>}
    </form>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function UserRolesAdmin() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => {
    fetch("/api/user-roles")
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((json) => {
        setUsers(json.data || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const handleToggle = async (user) => {
    setTogglingId(user.Id);
    try {
      const res = await fetch(`/api/user-roles/${user.Id}/toggle`, { method: "PATCH" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setUsers((prev) =>
        prev.map((u) => (u.Id === user.Id ? { ...u, IsActive: json.data.IsActive } : u))
      );
    } catch (err) {
      console.error("Toggle failed:", err);
    } finally {
      setTogglingId(null);
    }
  };

  const handleAddUser = (savedUser, wasUpdated) => {
    if (wasUpdated) {
      // Role was changed â€” update existing row
      setUsers((prev) => prev.map((u) => u.Id === savedUser.Id ? { ...u, ...savedUser } : u));
    } else {
      // New user â€” prepend
      setUsers((prev) => [savedUser, ...prev]);
    }
    setShowAddForm(false);
  };

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
    setPage(1);
  };

  const sortIcon = (col) => {
    if (sortCol !== col) return " ⇅";
    return sortDir === "asc" ? " ↑" : " ↓";
  };

  // Filter → search → sort → paginate
  const searched = users.filter((u) =>
    [u.Email, u.Role].some((v) =>
      String(v ?? "").toLowerCase().includes(search.toLowerCase())
    )
  );
  const roleFiltered =
    roleFilter === "All" ? searched : searched.filter((u) => u.Role === roleFilter);
  const statusFiltered =
    statusFilter === "All"
      ? roleFiltered
      : statusFilter === "Active"
        ? roleFiltered.filter((u) => u.IsActive)
        : roleFiltered.filter((u) => !u.IsActive);

  const sorted = [...statusFiltered].sort((a, b) => {
    if (!sortCol) return 0;
    const cmp = String(a[sortCol] ?? "").localeCompare(String(b[sortCol] ?? ""), undefined, {
      numeric: true,
    });
    return sortDir === "asc" ? cmp : -cmp;
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pageData = sorted.slice((page - 1) * pageSize, page * pageSize);
  const isEmpty = !loading && !error && users.length === 0;

  if (loading) {
    return (
      <div className="text-center">
        <img src="/images/lnw-loader.gif" alt="Loading.." />
        <h2 className="text-center">Loading..!</h2>
      </div>
    );
  }

  if (error) return <div className="triage-error">Error: {error}</div>;

  return (
    <div className="p-3">
      <h3>User Management</h3>



      <div className="triage-filter">
        <input
          className="triage-search"
          type="search"
          placeholder="🔍  Search by email or role…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        <select
          className="triage-select"
          value={roleFilter}
          onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
        >
          <option value="All">All Roles</option>
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select
          className="triage-select"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
        >
          <option value="All">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
        <button
          type="button"
          className="add-btn"
          onClick={() => setShowAddForm((v) => !v)}
        >
          {showAddForm ? "Cancel" : "Add / Update User"}
        </button>
      </div>

      {showAddForm && (
        <AddUserForm onAdd={handleAddUser} onCancel={() => setShowAddForm(false)} />
      )}

      {isEmpty && !showAddForm && (
        <div className="triage-empty-state">
          <svg width="52" height="52" fill="none" viewBox="0 0 24 24">
            <rect x="3" y="3" width="18" height="18" rx="3" stroke="#cbd5e1" strokeWidth="1.5" />
            <path d="M3 8h18" stroke="#cbd5e1" strokeWidth="1.5" />
            <path d="M8 12h8M8 16h5" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <p className="no-records">No users found.</p>
          <p className="no-records-subtitile">Use "+ Add User" to create the first entry.</p>
        </div>
      )}

      {!isEmpty && (
        <div className="table-wrapper">
          <table className="triage-table">
            <thead>
              <tr>
                <th onClick={() => handleSort("Email")} style={{ cursor: "pointer" }}>
                  Email<span className="sort-icon">{sortIcon("Email")}</span>
                </th>
                <th onClick={() => handleSort("Role")} style={{ cursor: "pointer" }}>
                  Role<span className="sort-icon">{sortIcon("Role")}</span>
                </th>
                <th onClick={() => handleSort("CreatedDate")} style={{ cursor: "pointer" }}>
                  Created Date<span className="sort-icon">{sortIcon("CreatedDate")}</span>
                </th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {pageData.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center">No matching records.</td>
                </tr>
              ) : (
                pageData.map((user) => (
                  <tr key={user.Id} className="triage-row">
                    <td className="triage-td" title={user.Email}>{user.Email}</td>
                    <td className="triage-td">{user.Role}</td>
                    <td className="triage-td">{formatDate(user.CreatedDate)}</td>
                    <td className="triage-td">
                      <ActiveToggle
                        isActive={!!user.IsActive}
                        onToggle={() => handleToggle(user)}
                        loading={togglingId === user.Id}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="d-flex p-3">
        {!isEmpty && totalPages > 1 && (
          <div className="triage-pagination">
            <button onClick={() => setPage(1)} disabled={page === 1}>«</button>
            <button onClick={() => setPage((p) => p - 1)} disabled={page === 1}>‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1)
                  acc.push(<span key={`e-${p}`} className="pg-info">…</span>);
                acc.push(
                  <button
                    key={p}
                    className={`pagination-btn ${p === page ? "active" : ""}`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                );
                return acc;
              }, [])}
            <button onClick={() => setPage((p) => p + 1)} disabled={page === totalPages}>›</button>
            <button onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
            <span className="pg-info">
              {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, sorted.length)} of{" "}
              {sorted.length}
            </span>
          </div>
        )}
        <div className="triage-row-per-page">
          <label className="page-size-label">Rows per page:</label>
          <select
            className="triage-select"
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
          >
            {[10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d)) return value;
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};
